import { Link } from 'react-router-dom';
import type { RankingItem } from '@/types/content';

function formatHeat(heat: number) {
  return `${(heat / 10000).toFixed(1)} 万`;
}

function rankStyle(rank: number) {
  if (rank === 1) return 'bg-primary-500 text-background-50';
  if (rank === 2) return 'bg-primary-300 text-foreground-950';
  if (rank === 3) return 'bg-accent-500 text-background-50';
  return 'bg-background-200 text-foreground-700';
}

function TrendFlag({ trend, change }: { trend: RankingItem['trend']; change: number }) {
  if (trend === 'new') {
    return <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[11px] text-accent-900">新上榜</span>;
  }
  if (trend === 'up') {
    return (
      <span className="flex items-center gap-0.5 text-[11px] text-primary-700">
        <span className="flex h-3 w-3 items-center justify-center">
          <i className="ri-arrow-up-line text-xs" />
        </span>
        {change}
      </span>
    );
  }
  if (trend === 'down') {
    return (
      <span className="flex items-center gap-0.5 text-[11px] text-accent-700">
        <span className="flex h-3 w-3 items-center justify-center">
          <i className="ri-arrow-down-line text-xs" />
        </span>
        {change}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-0.5 text-[11px] text-foreground-400">
      <span className="flex h-3 w-3 items-center justify-center">
        <i className="ri-subtract-line text-xs" />
      </span>
      持平
    </span>
  );
}

export default function RankingList({ items }: { items: RankingItem[] }) {
  const maxHeat = items.reduce((max, item) => (item.heat > max ? item.heat : max), 1);

  return (
    <ol className="flex flex-col">
      {items.map((item, index) => (
        <li
          key={item.slug}
          className={`flex items-center gap-3 py-3 ${index !== items.length - 1 ? 'border-b border-background-200' : ''}`}
        >
          <span
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm font-bold ${rankStyle(item.rank)}`}
          >
            {item.rank}
          </span>
          <div className="min-w-0 flex-1">
            <Link
              to={`/topic/${item.slug}`}
              className="line-clamp-1 cursor-pointer text-sm font-medium text-foreground-900 transition-colors hover:text-primary-700 md:text-base"
            >
              {item.title}
            </Link>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="h-1 w-full max-w-[220px] overflow-hidden rounded-full bg-background-200">
                <span
                  className="block h-full rounded-full bg-primary-400"
                  style={{ width: `${Math.max(8, Math.round((item.heat / maxHeat) * 100))}%` }}
                />
              </span>
              <span className="shrink-0 text-[11px] text-foreground-500">{formatHeat(item.heat)}</span>
            </div>
          </div>
          <div className="shrink-0 pl-1">
            <TrendFlag trend={item.trend} change={item.change} />
          </div>
        </li>
      ))}
    </ol>
  );
}